// ============================================
// SITE OF SERVICE — OUTPUT GENERATOR
// Template-based: no AI call, runs instantly
// Produces speakable, short-line P2P content
// Supports PROACTIVE and SALVAGE variants
// ============================================

import type {
    SiteOfServiceInputs,
    SiteOfServiceOutput,
    LeverageClassification,
    CarePathwayBullet,
    P2PQuestion,
    P2PQuestionCategory,
    SoSDimension,
    ArgumentStep,
    SoSVariant,
  } from "../types/siteOfService";
  import { classifySiteOfServiceLeverage } from "./siteOfServiceClassifier";
  
  // ============================================
  // HELPERS
  // ============================================
  
  function truncate(text: string, maxWords: number): string {
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text.trim();
    return words.slice(0, maxWords).join(" ") + "…";
  }
  
  function isFilled(text: string): boolean {
    if (!text) return false;
    const lower = text.trim().toLowerCase();
    return lower.length > 0 && lower !== "unknown" && lower !== "n/a" && lower !== "na" && lower !== "not sure";
  }
  
  function isUnknown(text: string): boolean {
    if (!text) return true;
    const lower = text.trim().toLowerCase();
    return lower === "" || lower === "unknown" || lower === "not sure" || lower === "n/a" || lower === "na";
  }
  
  /** Extract short chip-sized fragments from a longer input string.
   *  Splits on semicolons, commas, periods — keeps each fragment ≤6 words. */
  function toChips(text: string, maxChips = 3): string[] {
    if (!text || !isFilled(text)) return [];
    const raw = text
      .split(/[;.,]\s*/)
      .map((s) => s.trim())
      .filter((s) => s.length > 2);
  
    const chips: string[] = [];
    for (const frag of raw) {
      if (chips.length >= maxChips) break;
      const words = frag.split(/\s+/);
      if (words.length <= 6) {
        chips.push(frag.toLowerCase());
      } else {
        chips.push(words.slice(0, 6).join(" ").toLowerCase());
      }
    }
    return chips;
  }
  
  /** Parse priorAttempts string to number */
  function parseAttempts(text: string): number {
    if (!text) return 0;
    const clean = text.trim().replace("+", "");
    const n = parseInt(clean, 10);
    return isNaN(n) ? 0 : n;
  }
  
  // ############################################################
  //
  //  PROACTIVE VARIANT — all existing logic preserved
  //
  // ############################################################
  
  // ============================================
  // CARE PATHWAY BULLETS (max 6) — proactive
  // ============================================
  
  function buildCarePathwayBullets(inputs: SiteOfServiceInputs, classification: LeverageClassification): CarePathwayBullet[] {
    const bullets: CarePathwayBullet[] = [];
    const factors = new Set(classification.contributingFactors);
  
    if (factors.has("monitoring_differential") && isFilled(inputs.monitoringPreferred)) {
      const text = isUnknown(inputs.monitoringRedirected)
        ? `Preferred site: ${truncate(inputs.monitoringPreferred, 18)}. Redirected site: protocol unknown.`
        : `Preferred site: ${truncate(inputs.monitoringPreferred, 14)}. Redirected: ${truncate(inputs.monitoringRedirected, 10)}.`;
      bullets.push({ dimension: "monitoring_differential", label: "Monitoring difference", text });
    }
  
    if (factors.has("rescue_window") && isFilled(inputs.rescueWindow)) {
      bullets.push({
        dimension: "rescue_window",
        label: "Rescue difference",
        text: truncate(inputs.rescueWindow, 20),
      });
    }
  
    if (factors.has("timing_delay") && isFilled(inputs.timingSensitivity)) {
      bullets.push({
        dimension: "timing_delay",
        label: "Time-to-intervention risk",
        text: truncate(inputs.timingSensitivity, 20),
      });
    }
  
    if (factors.has("procedure_coupling") && isFilled(inputs.procedureCoupling)) {
      bullets.push({
        dimension: "procedure_coupling",
        label: "Coupling / staging impact",
        text: truncate(inputs.procedureCoupling, 20),
      });
    }
  
    if (factors.has("team_protocol") && isFilled(inputs.teamProtocol)) {
      bullets.push({
        dimension: "team_protocol",
        label: "Team / protocol dependency",
        text: truncate(inputs.teamProtocol, 20),
      });
    }
  
    if (factors.has("patient_vulnerability") && isFilled(inputs.patientVulnerability)) {
      const text = inputs.patientVulnerability.trim();
      const parts = text.split(/[.;]\s*/);
      if (parts.length >= 2) {
        bullets.push({
          dimension: "patient_vulnerability",
          label: "Patient-specific factor",
          text: truncate(parts[0], 18),
        });
        bullets.push({
          dimension: "irreversible_consequence",
          label: "Irreversible risk if delayed/missed",
          text: truncate(parts.slice(1).join(". "), 18),
        });
      } else {
        bullets.push({
          dimension: "patient_vulnerability",
          label: "Patient-specific factor",
          text: truncate(text, 20),
        });
      }
    }
  
    return bullets.slice(0, 6);
  }
  
  // ============================================
  // P2P QUESTIONS — proactive
  // ============================================
  
  function buildP2PQuestions(inputs: SiteOfServiceInputs, classification: LeverageClassification): P2PQuestion[] {
    const questions: P2PQuestion[] = [];
    const factors = new Set(classification.contributingFactors);
    let priority = 1;
  
    if (isUnknown(inputs.monitoringRedirected)) {
      questions.push({
        category: "alternative_plan",
        text: "At the redirected facility, what is the first-24-hour monitoring protocol for this case?",
        priority: priority++,
      });
      questions.push({
        category: "alternative_plan",
        text: "Who performs postoperative checks and how often?",
        priority: priority++,
      });
      questions.push({
        category: "alternative_plan",
        text: "What is the escalation pathway if a complication is suspected?",
        priority: priority++,
      });
    } else if (factors.has("monitoring_differential")) {
      questions.push({
        category: "alternative_plan",
        text: "Can you confirm the postoperative monitoring frequency and staffing model at the redirected facility?",
        priority: priority++,
      });
      questions.push({
        category: "alternative_plan",
        text: "What is the escalation pathway if a complication is suspected at that site?",
        priority: priority++,
      });
    }
  
    if (factors.has("rescue_window") || isFilled(inputs.rescueWindow)) {
      questions.push({
        category: "rescue_capability",
        text: "If a complication occurs, what is the expected time to definitive intervention at the redirected facility?",
        priority: priority++,
      });
      questions.push({
        category: "rescue_capability",
        text: "Is immediate return-to-OR available at that site, and who is the responsible surgeon?",
        priority: priority++,
      });
    }
  
    if (factors.has("timing_delay") || isFilled(inputs.timingSensitivity)) {
      questions.push({
        category: "timing",
        text: "What is the expected scheduling interval at the redirected site?",
        priority: priority++,
      });
      questions.push({
        category: "timing",
        text: "How is clinical risk managed during any delay in scheduling?",
        priority: priority++,
      });
    }
  
    if (factors.has("procedure_coupling") || isFilled(inputs.procedureCoupling)) {
      questions.push({
        category: "procedure_coupling",
        text: "Will this require separation into multiple anesthetics at the redirected site?",
        priority: priority++,
      });
    }
  
    questions.push({
      category: "documentation",
      text: "Can you confirm your name and credentials for my documentation?",
      priority: priority++,
    });
  
    return questions.sort((a, b) => a.priority - b.priority).slice(0, 10);
  }
  
  // ============================================
  // ONE-LINER — proactive
  // ============================================
  
  function buildOneLiner(inputs: SiteOfServiceInputs, classification: LeverageClassification): string {
    const factors = new Set(classification.contributingFactors);
  
    if (classification.level === "LOW") {
      return "The clinical basis for site-of-care equivalence has not been established for this patient.";
    }
  
    const primary = factors.has("rescue_window")
      ? "rescue capability"
      : factors.has("monitoring_differential")
      ? "monitoring requirements"
      : factors.has("timing_delay")
      ? "time-to-intervention"
      : factors.has("procedure_coupling")
      ? "procedure staging"
      : "care pathway requirements";
  
    const vuln = isFilled(inputs.patientVulnerability)
      ? truncate(inputs.patientVulnerability.split(/[.;]/)[0], 6)
      : "this patient's needs";
  
    return `${vuln} requires ${primary} that differs between these facilities.`;
  }
  
  // ============================================
  // ARGUMENT STEPS — proactive
  // ============================================
  
  function buildArgumentSteps(
    inputs: SiteOfServiceInputs,
    classification: LeverageClassification,
    oneLiner: string
  ): ArgumentStep[] {
    const factors = new Set(classification.contributingFactors);
    const steps: ArgumentStep[] = [];
    let n = 1;
  
    // STEP 1 — Establish non-equivalence (Say)
    const vulnerability = isFilled(inputs.patientVulnerability)
      ? truncate(inputs.patientVulnerability.split(/[.;]/)[0], 8)
      : "this clinical scenario";
  
    const keyDiff = factors.has("rescue_window")
      ? "rescue capability"
      : factors.has("monitoring_differential")
      ? "postoperative monitoring"
      : factors.has("timing_delay")
      ? "time-to-intervention"
      : factors.has("procedure_coupling")
      ? "procedure staging"
      : "care pathway requirements";
  
    const step1Line = classification.level === "LOW"
      ? "I'd like to understand how clinical equivalence was determined between these two sites for this patient."
      : `These two sites are not clinically interchangeable for this patient — ${vulnerability} requires ${keyDiff} that differs between facilities.`;
  
    const step1Chips: string[] = [];
    step1Chips.push(...toChips(inputs.patientVulnerability, 2));
    if (isFilled(inputs.procedureCoupling)) step1Chips.push(...toChips(inputs.procedureCoupling, 1));
    if (isFilled(inputs.teamProtocol)) step1Chips.push(...toChips(inputs.teamProtocol, 1));
  
    steps.push({
      stepNumber: n++,
      title: "Establish non-equivalence",
      intent: "say",
      spokenLine: step1Line,
      evidenceChips: step1Chips.slice(0, 4),
      followUpQuestions: [],
    });
  
    // STEP 2 — Monitoring challenge (Ask)
    const step2Line = isUnknown(inputs.monitoringRedirected)
      ? "Can you describe the monitoring protocol, staffing model, and check frequency at the redirected facility for the first 24 hours?"
      : "Can you confirm the monitoring frequency and escalation pathway at the redirected facility match what this patient requires?";
  
    const step2Chips: string[] = [];
    if (isFilled(inputs.monitoringPreferred)) step2Chips.push(...toChips(inputs.monitoringPreferred, 2));
    if (isUnknown(inputs.monitoringRedirected)) {
      step2Chips.push("redirected protocol unknown");
    } else if (isFilled(inputs.monitoringRedirected)) {
      step2Chips.push(...toChips(inputs.monitoringRedirected, 1));
    }
  
    const step2Questions: string[] = [];
    if (isUnknown(inputs.monitoringRedirected)) {
      step2Questions.push("Who performs postoperative checks and how often?");
      step2Questions.push("What is the escalation pathway if a complication is suspected?");
    } else if (factors.has("monitoring_differential")) {
      step2Questions.push("What is the escalation pathway if a complication is suspected at that site?");
    }
  
    steps.push({
      stepNumber: n++,
      title: "Monitoring challenge",
      intent: "ask",
      spokenLine: step2Line,
      evidenceChips: step2Chips.slice(0, 4),
      followUpQuestions: step2Questions,
    });
  
    // STEP 3 — Rescue capability (Escalate)
    const step3Line = isFilled(inputs.rescueWindow)
      ? "If a complication occurs at the redirected site, what is the expected time to definitive intervention?"
      : "What on-site resources are available for complication management at the redirected facility?";
  
    const step3Chips: string[] = [];
    if (isFilled(inputs.rescueWindow)) step3Chips.push(...toChips(inputs.rescueWindow, 2));
    if (isFilled(inputs.timingSensitivity)) step3Chips.push(...toChips(inputs.timingSensitivity, 2));
  
    const step3Questions: string[] = [];
    if (isFilled(inputs.rescueWindow)) {
      step3Questions.push("Is immediate return-to-OR available, and who is the responsible surgeon?");
    }
    if (isFilled(inputs.timingSensitivity)) {
      step3Questions.push("What is the expected scheduling interval at the redirected site?");
      step3Questions.push("How is clinical risk managed during any delay?");
    }
    if (isFilled(inputs.procedureCoupling)) {
      step3Questions.push("Will this require separation into multiple anesthetics at the redirected site?");
    }
  
    steps.push({
      stepNumber: n++,
      title: "Rescue capability",
      intent: "escalate",
      spokenLine: step3Line,
      evidenceChips: step3Chips.slice(0, 4),
      followUpQuestions: step3Questions,
    });
  
    // STEP 4 — Responsibility shift (Confirm)
    const diffSummary = factors.has("monitoring_differential")
      ? "the monitoring differences we've discussed"
      : factors.has("rescue_window")
      ? "the rescue capability differences we've discussed"
      : factors.has("timing_delay")
      ? "the timing risk we've discussed"
      : "the care pathway differences we've discussed";
  
    const step4Line = classification.level === "LOW"
      ? "Can you confirm you're recommending the redirected site as clinically equivalent for this patient?"
      : `To confirm — you're recommending the redirected facility for this patient despite ${diffSummary}?`;
  
    steps.push({
      stepNumber: n++,
      title: "Responsibility shift",
      intent: "confirm",
      spokenLine: step4Line,
      evidenceChips: [],
      followUpQuestions: [],
    });
  
    // STEP 5 — Documentation lock (Document aloud)
    const docDetails = factors.has("monitoring_differential")
      ? "the monitoring protocol and staffing model as you've described"
      : factors.has("rescue_window")
      ? "the rescue and intervention timeline as you've described"
      : "the clinical capabilities as you've described";
  
    const step5Line = `I'll document that the recommended plan is management at the redirected facility with ${docDetails}.`;
  
    steps.push({
      stepNumber: n++,
      title: "Documentation lock",
      intent: "document_aloud",
      spokenLine: step5Line,
      evidenceChips: [],
      followUpQuestions: [
        "Is there anything you'd like me to add or correct in that documentation?",
      ],
    });
  
    return steps;
  }
  
  // ############################################################
  //
  //  SALVAGE VARIANT — failed access / delay
  //
  // ############################################################
  
  // ============================================
  // CARE PATHWAY BULLETS — salvage
  // ============================================
  
  function buildSalvageBullets(inputs: SiteOfServiceInputs, classification: LeverageClassification): CarePathwayBullet[] {
    const bullets: CarePathwayBullet[] = [];
    const factors = new Set(classification.contributingFactors);
    const attempts = parseAttempts(inputs.priorAttempts);
  
    // Documented failure
    if (factors.has("documented_failure")) {
      bullets.push({
        dimension: "documented_failure",
        label: "Access failure pattern",
        text: `${attempts} cancellation(s) at approved facility. Reasons: ${truncate(inputs.failureReasons, 16)}.`,
      });
    }
  
    // Elapsed delay
    if (factors.has("elapsed_delay") && isFilled(inputs.elapsedDelay)) {
      bullets.push({
        dimension: "elapsed_delay",
        label: "Elapsed delay",
        text: `${inputs.elapsedDelay.trim()} since originally scheduled date.`,
      });
    }
  
    // Clinical deterioration
    if (factors.has("clinical_deterioration") && isFilled(inputs.clinicalDeterioration)) {
      bullets.push({
        dimension: "clinical_deterioration",
        label: "Clinical change during delay",
        text: truncate(inputs.clinicalDeterioration, 22),
      });
    }
  
    // Alternative facility
    if (factors.has("alternative_available") && isFilled(inputs.alternativeSiteCapability)) {
      bullets.push({
        dimension: "alternative_available",
        label: "Alternative facility capability",
        text: truncate(inputs.alternativeSiteCapability, 20),
      });
    }
  
    // Shared: monitoring
    if (factors.has("monitoring_differential") && isFilled(inputs.monitoringPreferred)) {
      const text = isUnknown(inputs.monitoringRedirected)
        ? `Alternative site: ${truncate(inputs.monitoringPreferred, 14)}. Failed site: protocol unknown.`
        : `Alternative site: ${truncate(inputs.monitoringPreferred, 12)}. Failed site: ${truncate(inputs.monitoringRedirected, 8)}.`;
      bullets.push({ dimension: "monitoring_differential", label: "Monitoring at alternative", text });
    }
  
    // Shared: patient vulnerability
    if (factors.has("patient_vulnerability") && isFilled(inputs.patientVulnerability)) {
      bullets.push({
        dimension: "patient_vulnerability",
        label: "Patient-specific risk",
        text: truncate(inputs.patientVulnerability, 20),
      });
    }
  
    return bullets.slice(0, 6);
  }
  
  // ============================================
  // P2P QUESTIONS — salvage
  // ============================================
  
  function buildSalvageQuestions(inputs: SiteOfServiceInputs, classification: LeverageClassification): P2PQuestion[] {
    const questions: P2PQuestion[] = [];
    let priority = 1;
    const attempts = parseAttempts(inputs.priorAttempts);
  
    // Access failure questions
    questions.push({
      category: "access_failure",
      text: `This patient's surgery has been canceled ${attempts} time(s) at the approved facility. What is the plan to ensure it proceeds on the next attempt?`,
      priority: priority++,
    });
  
    if (isFilled(inputs.failureReasons)) {
      questions.push({
        category: "access_failure",
        text: "Has the issue that caused the previous cancellation(s) been resolved at the approved facility?",
        priority: priority++,
      });
    }
  
    questions.push({
      category: "access_failure",
      text: "At what point does repeated failure to deliver care at the approved site warrant a single-case agreement at an alternative facility?",
      priority: priority++,
    });
  
    // Clinical consequence questions
    if (isFilled(inputs.clinicalDeterioration)) {
      questions.push({
        category: "clinical_consequence",
        text: "What is the clinical plan for managing this patient's condition if surgery is canceled again?",
        priority: priority++,
      });
      questions.push({
        category: "clinical_consequence",
        text: "Who assumes clinical responsibility for adverse outcomes caused by further delay?",
        priority: priority++,
      });
    }
  
    // Alternative facility questions
    if (isFilled(inputs.alternativeSiteCapability)) {
      questions.push({
        category: "alternative_facility",
        text: "What specific clinical objection exists to authorizing this case at the alternative facility?",
        priority: priority++,
      });
      questions.push({
        category: "alternative_facility",
        text: "Is the concern cost-related? The facility fee at an outpatient surgery center is typically lower than the hospital rate.",
        priority: priority++,
      });
    }
  
    // Documentation
    questions.push({
      category: "documentation",
      text: "Can you confirm your name and credentials for my documentation of this discussion?",
      priority: priority++,
    });
  
    return questions.sort((a, b) => a.priority - b.priority).slice(0, 10);
  }
  
  // ============================================
  // ONE-LINER — salvage
  // ============================================
  
  function buildSalvageOneLiner(inputs: SiteOfServiceInputs, classification: LeverageClassification): string {
    const attempts = parseAttempts(inputs.priorAttempts);
  
    if (classification.level === "LOW") {
      return "The approved facility has been unable to deliver scheduled care; an alternative site with immediate availability exists.";
    }
  
    const delay = isFilled(inputs.elapsedDelay) ? ` over ${inputs.elapsedDelay.trim()}` : "";
    const deterioration = isFilled(inputs.clinicalDeterioration)
      ? `, with ${truncate(inputs.clinicalDeterioration.split(/[.;]/)[0], 8)} during the delay`
      : "";
  
    return `${attempts} cancellation(s) at the approved facility${delay}${deterioration} — alternative facility with immediate availability requested.`;
  }
  
  // ============================================
  // ARGUMENT STEPS — salvage (5-step path)
  // ============================================
  
  function buildSalvageArgumentSteps(
    inputs: SiteOfServiceInputs,
    classification: LeverageClassification
  ): ArgumentStep[] {
    const factors = new Set(classification.contributingFactors);
    const steps: ArgumentStep[] = [];
    let n = 1;
    const attempts = parseAttempts(inputs.priorAttempts);
    const attemptsText = attempts >= 3 ? `${attempts} times` : attempts === 2 ? "twice" : "once";
  
    // --------------------------------------------------
    // STEP 1 — Establish failure pattern (Say)
    // --------------------------------------------------
  
    let step1Line: string;
    if (attempts >= 3) {
      step1Line = `This patient's surgery has been canceled ${attemptsText} at the approved facility. We've exhausted reasonable attempts to deliver care at that site.`;
    } else if (attempts === 2) {
      const reason = isFilled(inputs.failureReasons)
        ? ` due to ${truncate(inputs.failureReasons.split(/[;,]/)[0], 8)}`
        : "";
      step1Line = `This patient's surgery has been canceled twice at the approved facility${reason}. We need to discuss an alternative path.`;
    } else {
      const reason = isFilled(inputs.failureReasons)
        ? ` due to ${truncate(inputs.failureReasons.split(/[;,]/)[0], 8)}`
        : "";
      const delay = isFilled(inputs.elapsedDelay) ? `, creating a ${inputs.elapsedDelay.trim()} delay` : "";
      step1Line = `This patient's surgery was canceled at the approved facility${reason}${delay}.`;
    }
  
    steps.push({
      stepNumber: n++,
      title: "Establish failure pattern",
      intent: "say",
      spokenLine: step1Line,
      evidenceChips: [],
      followUpQuestions: isFilled(inputs.failureReasons) ? [
        "Has the issue that caused the cancellation(s) been resolved at the approved facility?",
      ] : [],
    });
  
    // --------------------------------------------------
    // STEP 2 — Clinical consequence of delay (Say)
    // --------------------------------------------------
  
    let step2Line: string;
    if (isFilled(inputs.clinicalDeterioration)) {
      const firstFact = truncate(inputs.clinicalDeterioration.split(/[.;]/)[0], 14);
      const delay = isFilled(inputs.elapsedDelay) ? inputs.elapsedDelay.trim() : "the";
      step2Line = `During the ${delay} delay, ${firstFact}. This is no longer a scheduling issue — this is active clinical harm.`;
    } else if (isFilled(inputs.elapsedDelay)) {
      step2Line = `This patient has been waiting ${inputs.elapsedDelay.trim()} since the originally scheduled date. What is the clinical justification for further delay?`;
    } else {
      step2Line = "The delay created by this cancellation introduces clinical risk that needs to be addressed.";
    }
  
    const step2Questions: string[] = [];
    step2Questions.push("What is the clinical plan for managing this patient if the surgery is canceled again?");
    if (isFilled(inputs.clinicalDeterioration)) {
      step2Questions.push("Who assumes clinical responsibility for adverse outcomes caused by further delay?");
    }
  
    steps.push({
      stepNumber: n++,
      title: "Clinical consequence of delay",
      intent: "say",
      spokenLine: step2Line,
      evidenceChips: [],
      followUpQuestions: step2Questions,
    });
  
    // --------------------------------------------------
    // STEP 3 — Present the alternative (Ask)
    // --------------------------------------------------
  
    let step3Line: string;
    if (isFilled(inputs.alternativeSiteCapability)) {
      const capability = truncate(inputs.alternativeSiteCapability.split(/[.;]/)[0], 12);
      step3Line = `I have immediate surgical availability at an alternative facility — ${capability}. What clinical objection exists to authorizing this case there?`;
    } else {
      step3Line = "I have immediate surgical availability at an alternative CMS-certified facility where I perform this same procedure. What clinical objection exists to authorizing this case there?";
    }
  
    const step3Questions: string[] = [
      "Is the concern cost? The facility fee at an outpatient surgery center is typically lower than the hospital rate.",
      "Is the concern network status? The surgeon, procedure, and clinical protocol are identical — only the facility differs.",
    ];
    if (isFilled(inputs.rescueWindow)) {
      step3Questions.push(`The alternative site offers: ${truncate(inputs.rescueWindow, 12)}.`);
    }
    if (isFilled(inputs.monitoringPreferred)) {
      step3Questions.push(`Postop monitoring at the alternative: ${truncate(inputs.monitoringPreferred, 12)}.`);
    }
  
    steps.push({
      stepNumber: n++,
      title: "Present the alternative",
      intent: "ask",
      spokenLine: step3Line,
      evidenceChips: [],
      followUpQuestions: step3Questions,
    });
  
    // --------------------------------------------------
    // STEP 4 — Accountability (Confirm)
    // --------------------------------------------------
  
    let step4Line: string;
    if (attempts >= 2) {
      step4Line = `To confirm — you're recommending we attempt surgery again at the same facility that has already failed to deliver care ${attemptsText} for this patient?`;
    } else {
      const consequence = isFilled(inputs.clinicalDeterioration)
        ? "the clinical changes during the delay we've discussed"
        : "the delay this has already caused";
      step4Line = `To confirm — you're recommending we reschedule at the facility that canceled, despite ${consequence}?`;
    }
  
    steps.push({
      stepNumber: n++,
      title: "Accountability",
      intent: "confirm",
      spokenLine: step4Line,
      evidenceChips: [],
      followUpQuestions: [],
    });
  
    // --------------------------------------------------
    // STEP 5 — Documentation lock (Document aloud)
    // --------------------------------------------------
  
    const delayNote = isFilled(inputs.elapsedDelay) ? `a ${inputs.elapsedDelay.trim()} elapsed delay` : "an ongoing delay";
    const deteriorationNote = isFilled(inputs.clinicalDeterioration)
      ? ` and ${truncate(inputs.clinicalDeterioration.split(/[.;]/)[0], 10)} during that delay`
      : "";
  
    const step5Line = `I'll document that the recommended plan is to continue attempting surgery at the approved facility, with ${attempts} prior cancellation(s), ${delayNote}${deteriorationNote}. I'll also note that an alternative facility with immediate availability was discussed and declined.`;
  
    steps.push({
      stepNumber: n++,
      title: "Documentation lock",
      intent: "document_aloud",
      spokenLine: step5Line,
      evidenceChips: [],
      followUpQuestions: [
        "Is there anything you'd like me to add or correct in that documentation?",
      ],
    });
  
    return steps;
  }
  
  // ############################################################
  //
  //  MAIN GENERATOR — routes by variant
  //
  // ############################################################
  
  export function generateSiteOfServiceOutput(inputs: SiteOfServiceInputs): SiteOfServiceOutput {
    const classification = classifySiteOfServiceLeverage(inputs);
  
    if (inputs.variant === "SALVAGE") {
      const carePathwayDiffs = buildSalvageBullets(inputs, classification);
      const p2pQuestions = buildSalvageQuestions(inputs, classification);
      const oneLiner = buildSalvageOneLiner(inputs, classification);
      const argumentSteps = buildSalvageArgumentSteps(inputs, classification);
      return { classification, carePathwayDiffs, p2pQuestions, oneLiner, argumentSteps };
    }
  
    // Proactive (default)
    const carePathwayDiffs = buildCarePathwayBullets(inputs, classification);
    const p2pQuestions = buildP2PQuestions(inputs, classification);
    const oneLiner = buildOneLiner(inputs, classification);
    const argumentSteps = buildArgumentSteps(inputs, classification, oneLiner);
    return { classification, carePathwayDiffs, p2pQuestions, oneLiner, argumentSteps };
  }
  
  // ############################################################
  //
  //  SERIALIZATION — pack/unpack for additional_context
  //
  // ############################################################
  
  const SOS_PREFIX = "SITE_OF_SERVICE_CONTEXT:";
  
  export function serializeSoSInputs(inputs: SiteOfServiceInputs): string {
    const parts: string[] = [];
  
    // Variant line — always first. Omit for PROACTIVE to maintain backward compat.
    if (inputs.variant === "SALVAGE") {
      parts.push("Variant: SALVAGE");
    }
  
    // Shared fields
    if (isFilled(inputs.rescueWindow)) parts.push(`Rescue window: ${inputs.rescueWindow}`);
    if (isFilled(inputs.monitoringPreferred)) parts.push(`Preferred-site monitoring: ${inputs.monitoringPreferred}`);
    if (isUnknown(inputs.monitoringRedirected)) {
      parts.push("Redirected-site monitoring: UNKNOWN");
    } else if (isFilled(inputs.monitoringRedirected)) {
      parts.push(`Redirected-site monitoring: ${inputs.monitoringRedirected}`);
    }
    if (isFilled(inputs.patientVulnerability)) parts.push(`Patient vulnerability: ${inputs.patientVulnerability}`);
  
    if (inputs.variant === "SALVAGE") {
      // Salvage-specific fields
      if (isFilled(inputs.priorAttempts)) parts.push(`Prior attempts: ${inputs.priorAttempts}`);
      if (isFilled(inputs.failureReasons)) parts.push(`Failure reasons: ${inputs.failureReasons}`);
      if (isFilled(inputs.elapsedDelay)) parts.push(`Elapsed delay: ${inputs.elapsedDelay}`);
      if (isFilled(inputs.clinicalDeterioration)) parts.push(`Clinical change during delay: ${inputs.clinicalDeterioration}`);
      if (isFilled(inputs.alternativeSiteCapability)) parts.push(`Alternative facility: ${inputs.alternativeSiteCapability}`);
    } else {
      // Proactive-specific fields
      if (isFilled(inputs.timingSensitivity)) parts.push(`Timing sensitivity: ${inputs.timingSensitivity}`);
      if (isFilled(inputs.procedureCoupling)) parts.push(`Procedure coupling: ${inputs.procedureCoupling}`);
      if (isFilled(inputs.teamProtocol)) parts.push(`Team/protocol dependency: ${inputs.teamProtocol}`);
    }
  
    if (parts.length === 0) return "";
    return `${SOS_PREFIX}\n${parts.join("\n")}`;
  }
  
  /** Create a blank inputs object for a given variant */
  export function createEmptySoSInputs(variant: SoSVariant = "PROACTIVE"): SiteOfServiceInputs {
    return {
      variant,
      // Shared
      rescueWindow: "",
      monitoringPreferred: "",
      monitoringRedirected: "",
      patientVulnerability: "",
      // Proactive
      timingSensitivity: "",
      procedureCoupling: "",
      teamProtocol: "",
      // Salvage
      priorAttempts: "",
      failureReasons: "",
      elapsedDelay: "",
      clinicalDeterioration: "",
      alternativeSiteCapability: "",
    };
  }
  
  export function parseSoSFromContext(additionalContext: string | null | undefined): SiteOfServiceInputs | null {
    if (!additionalContext) return null;
    const idx = additionalContext.indexOf(SOS_PREFIX);
    if (idx === -1) return null;
  
    const block = additionalContext.slice(idx + SOS_PREFIX.length).split(/\n---\n/)[0];
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
  
    // Detect variant
    const variantLine = lines.find((l) => l.startsWith("Variant:"));
    const variant: SoSVariant = variantLine?.includes("SALVAGE") ? "SALVAGE" : "PROACTIVE";
  
    const inputs = createEmptySoSInputs(variant);
  
    for (const line of lines) {
      // Shared fields
      if (line.startsWith("Rescue window:")) inputs.rescueWindow = line.replace("Rescue window:", "").trim();
      else if (line.startsWith("Preferred-site monitoring:")) inputs.monitoringPreferred = line.replace("Preferred-site monitoring:", "").trim();
      else if (line.startsWith("Redirected-site monitoring:")) {
        const val = line.replace("Redirected-site monitoring:", "").trim();
        inputs.monitoringRedirected = val === "UNKNOWN" ? "unknown" : val;
      }
      else if (line.startsWith("Patient vulnerability:")) inputs.patientVulnerability = line.replace("Patient vulnerability:", "").trim();
      // Proactive fields
      else if (line.startsWith("Timing sensitivity:")) inputs.timingSensitivity = line.replace("Timing sensitivity:", "").trim();
      else if (line.startsWith("Procedure coupling:")) inputs.procedureCoupling = line.replace("Procedure coupling:", "").trim();
      else if (line.startsWith("Team/protocol dependency:")) inputs.teamProtocol = line.replace("Team/protocol dependency:", "").trim();
      // Salvage fields
      else if (line.startsWith("Prior attempts:")) inputs.priorAttempts = line.replace("Prior attempts:", "").trim();
      else if (line.startsWith("Failure reasons:")) inputs.failureReasons = line.replace("Failure reasons:", "").trim();
      else if (line.startsWith("Elapsed delay:")) inputs.elapsedDelay = line.replace("Elapsed delay:", "").trim();
      else if (line.startsWith("Clinical change during delay:")) inputs.clinicalDeterioration = line.replace("Clinical change during delay:", "").trim();
      else if (line.startsWith("Alternative facility:")) inputs.alternativeSiteCapability = line.replace("Alternative facility:", "").trim();
    }
  
    // Check if anything meaningful was filled
    const { variant: _v, ...fields } = inputs;
    const anyFilled = Object.values(fields).some((v) => v && v !== "unknown" && v.trim().length > 0);
    return anyFilled ? inputs : null;
  }